/**
 * Schedule callback when main thread is idle.
 * @param {() => void} callback
 * @param {{ timeout?: number }} [options]
 * @returns {() => void} Canceler
 */
function scheduleIdle(callback, options = {}) {
  const timeout = options.timeout ?? 2000;
  if (typeof requestIdleCallback !== 'undefined') {
    const id = requestIdleCallback(callback, { timeout });
    return () => cancelIdleCallback(id);
  }

  const id = setTimeout(callback, 1);
  return () => clearTimeout(id);
}


(function () {
class RevealStack extends HTMLElement {
  constructor() {
    super();
  }

  connectedCallback() {
    document.fonts.ready.then(() => {
      this.init();
    });
  }

  init() {
    const children = Array.from(this.children);

    // Use reduce to keep track of total lines processed
    children.reduce((totalLines, child, index) => {
      // Only split text if the element has data-split="true"
      if (child.hasAttribute('data-split') && child.textContent.trim()) {
        const splitText = new SplitText(child, {
          type: "lines",
          linesClass: "line line++",
          onSplit: (self) => {
            // Wrap each line in a div for better control
            self.lines.forEach(line => {
              line.classList.add('overflow-hidden');
              const wrapper = document.createElement('div');
              wrapper.classList.add('overflow-hidden');
              line.parentNode.insertBefore(wrapper, line);
              wrapper.appendChild(line);
            });

            // Animate each line with stagger, adding delay based on total lines processed
            gsap.from(self.lines, {
              duration: 0.8,
              y: '100%',
              opacity: 0,
              ease: 'power4.out',
              stagger: 0.15,
              delay: totalLines * 0.15,
              scrollTrigger: {
                trigger: this,
                start: 'top 80%',
                once: true
              },
              onComplete: () => {
                // Revert split text and remove wrappers
                // self.revert();
                // Remove overflow-hidden class from the original element
                child.classList.remove('overflow-hidden');
              }
            });

            // Store the split text instance for cleanup
            child._splitText = self;
          }
        });


        // Return updated total lines count
        return totalLines + splitText.lines.length;
      } else {
        // For non-split elements, wrap them in a container for overflow control
        // child has margins, move the margins to the wrapper
        const wrapper = document.createElement('div');
        const innerWrapper = document.createElement('div');
        wrapper.classList.add('overflow-hidden');
        wrapper.style.marginTop = window.getComputedStyle(child).marginTop;
        wrapper.style.marginBottom = window.getComputedStyle(child).marginBottom;
        wrapper.style.marginLeft = window.getComputedStyle(child).marginLeft;
        wrapper.style.marginRight = window.getComputedStyle(child).marginRight;
        child.style.margin = '0';
        child.parentNode.insertBefore(wrapper, child);
        innerWrapper.appendChild(child);
        wrapper.appendChild(innerWrapper);

        const y = child.dataset.y || '100%';

        // Animate with the same style as text, adding delay based on total lines processed
        gsap.from(innerWrapper, {
          duration: 0.8,
          y: y,
          opacity: 0,
          ease: 'power4.out',
          delay: totalLines * 0.15,
          scrollTrigger: {
            trigger: this,
            start: '10% 80%',
            once: true
          },
          onComplete: () => {
            // Move the child back to its original position
            const parent = wrapper.parentNode;
            parent.insertBefore(child, wrapper);
            // Restore original margins
            child.style.margin = '';
            // Remove the wrapper
            parent.removeChild(wrapper);
          }
        });

        // Return updated total lines count (increment by 1 for non-split elements)
        return totalLines + 1;
      }
    }, 0); // Start with 0 total lines

    // Clean up on unmount
    this.addEventListener('unmount', () => {
      children.forEach(child => {
        if (child._splitText) {
          child._splitText.revert();
        }
      });
    });
  }
}

customElements.define('reveal-stack', RevealStack);

class ParallaxBackground extends HTMLElement {
  constructor() {
    super();
    this.preloadBackgroundImage();
  }

  connectedCallback() {
    if (!this.closest('animated-section')) {
      this.init();
    }
  }

  preloadBackgroundImage() {
    // Get the background image URL from the style attribute
    const style = this.getAttribute('style');
    if (style && style.backgroundImage) {
      const imageUrl = style.backgroundImage.replace('url(', '').replace(')', '');
      if (imageUrl) {
        // Create link preload element for highest priority
        const link = document.createElement('link');
        link.rel = 'preload';
        link.as = 'image';
        link.href = imageUrl;
        link.fetchPriority = 'high';
        document.head.appendChild(link);

        // Also create an Image object as backup
        const img = new Image();
        img.src = imageUrl;
      }
    }
  }

  init() {
      let { backgroundSize = 100, y = 100 } = this.dataset;
      let fromBackgroundSize = '';
      let toBackgroundSize = '';

      // detect the aspect ratio of the background image and 'this's ratio and switch the place of 'auto' in the gsap set and gsap to.
      const backgroundImage = this.style.backgroundImage;
      const backgroundImageUrl = backgroundImage.replace('url(', '').replace(')', '');
      // Skip if no background image or regex match failed
      if (!backgroundImage || !backgroundImageUrl) {
        return;
      }

      const backgroundImageObj = new Image();
      backgroundImageObj.src = backgroundImageUrl;
      const backgroundImageAspectRatio = parseInt(this.dataset.width) / parseInt(this.dataset.height);
      const thisAspectRatio = this.offsetWidth / this.offsetHeight;
      if (backgroundImageAspectRatio > thisAspectRatio) {
        fromBackgroundSize = `auto ${backgroundSize}%`;
        toBackgroundSize = `auto 100%`;
      } else {
        fromBackgroundSize = `${backgroundSize}% auto`;
        toBackgroundSize = `100% auto`;
      }

      gsap.set(this, {
        backgroundPosition: `50% ${y}%`,
        backgroundSize: fromBackgroundSize
      })

      gsap.to(this, {
        backgroundPosition: '50% 50%',
        backgroundSize: toBackgroundSize,
        ease: 'none',
        scrollTrigger: {
          trigger: this,
          start: 'top bottom',
          end: 'bottom top',
          scrub: true
        }
      });
  }
}

customElements.define('parallax-background', ParallaxBackground);

class AnimatedSection extends HTMLElement {
  constructor() {
    super();
    this.parallaxItems = [];
  }

  connectedCallback() {
    ScrollTrigger.refresh();
    this.init();
  }

  init() {
    // Check if user prefers reduced motion
    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
      return;
    }

    this.initParallaxItems();
    this.initParallaxBackground();
    this.initFadeInItems();
  }

  initFadeInItems() {
    this.fadeInItems = Array.from(this.querySelectorAll('fade-in-item'));
    this.fadeInItems.forEach(item => {
      // Set initial state
      gsap.set(item, {
        opacity: 0,
        y: 30
      });

      // Create scroll trigger
      ScrollTrigger.create({
        trigger: item,
        start: 'top 80%',
        onEnter: () => {
          gsap.to(item, {
            opacity: 1,
            y: 0,
            duration: 1.2,
            ease: 'power2.out'
          });
        },
        once: true
      });
    });
  }

  initParallaxBackground() {
    this.parallaxBackgrounds = Array.from(this.querySelectorAll('parallax-background'))

    this.parallaxBackgrounds.forEach(background => {
      background.init();
    });
  }

  initParallaxItems() {
    this.parallaxItems = Array.from(this.querySelectorAll('parallax-item'));

    this.parallaxItems.forEach(item => {
      const yPercent = parseInt(item.dataset.y) || 100;

      // Set initial position
      gsap.set(item, {
        yPercent: -yPercent
      });

      // Animate to final position
      gsap.to(item, {
        yPercent: yPercent,
        ease: 'power2.inOut',
        scrollTrigger: {
          trigger: this,
          start: 'top bottom',
          end: 'bottom top',
          scrub: item.dataset.scrub || 0.8,
        }
      });
    });
  }
}

class ParallaxItem extends HTMLElement {
  constructor() {
    super();
  }
}

customElements.define('animated-section', AnimatedSection);
customElements.define('parallax-item', ParallaxItem);
})();
(function () {
/**
 * Electric Property Updater Web Component
 * 
 * Transfers data-* attributes from clicked buttons to a target element.
 * 
 * Usage:
 * <electric-property-updater data-target-selector="#target-element">
 *   <button data-url="/some-url" data-custom="value">Click me</button>
 * </electric-property-updater>
 */
class ElectricPropertyUpdater extends HTMLElement {
  constructor() {
    super();
    this.targetSelector = this.getAttribute('data-target-selector');
    this.targetElement = null;
    this.activeButton = null;
  }

  connectedCallback() {
    this.initialize();
  }

  disconnectedCallback() {
    this.removeEventListeners();
  }

  initialize() {
    // Find the target element
    this.targetElement = document.querySelector(this.targetSelector);
    
    if (!this.targetElement) {
      console.warn(`ElectricPropertyUpdater: Target element not found for selector "${this.targetSelector}"`);
      return;
    }

    // Find all buttons within this component
    const buttons = this.querySelectorAll('button');
    
    if (buttons.length === 0) {
      console.warn('ElectricPropertyUpdater: No buttons found within the component');
      return;
    }

    // Set the first button as active by default
    this.activeButton = buttons[0];
    this.updateTargetProperties(this.activeButton);
    this.updateButtonStates();

    // Add click listeners to all buttons
    buttons.forEach(button => {
      button.addEventListener('click', this.handleButtonClick.bind(this));
    });
  }

  handleButtonClick(event) {
    const button = event.currentTarget;
    
    // Update active button
    this.activeButton = button;
    
    // Update target element properties
    this.updateTargetProperties(button);
    
    // Update button visual states
    this.updateButtonStates();
  }

  updateTargetProperties(button) {
    if (!this.targetElement) return;

    // Get all data-* attributes from the button
    const dataAttributes = Array.from(button.attributes)
      .filter(attr => attr.name.startsWith('data-'))
      .filter(attr => attr.name !== 'data-target-selector'); // Exclude our own attribute

    // Apply each data attribute to the target element
    dataAttributes.forEach(attr => {
      this.targetElement.setAttribute(attr.name, attr.value);
    });

    // Dispatch a custom event to notify other components
    this.targetElement.dispatchEvent(new CustomEvent('electric-property-updated', {
      detail: {
        sourceButton: button,
        attributes: Object.fromEntries(
          dataAttributes.map(attr => [attr.name, attr.value])
        )
      },
      bubbles: true
    }));
  }

  updateButtonStates() {
    const buttons = this.querySelectorAll('button');
    
    buttons.forEach(button => {
      // Remove active state classes
      button.classList.remove('underline', 'active', 'text-primary');
      
      // Add active state to current button
      if (button === this.activeButton) {
        button.classList.add('underline');
      }
    });
  }

  removeEventListeners() {
    const buttons = this.querySelectorAll('button');
    buttons.forEach(button => {
      button.removeEventListener('click', this.handleButtonClick.bind(this));
    });
  }
}

// Register the web component
customElements.define('electric-property-updater', ElectricPropertyUpdater);
})();
(function () {
/**
 * ElectricDOMReady Web Component
 * 
 * Handles scripts that need to run when DOM is ready, particularly useful
 * for injected content where DOMContentLoaded has already fired.
 * This component runs initialization scripts when connected to the DOM.
 * 
 * @example
 * <electric-dom-ready></electric-dom-ready>
 */
class ElectricDOMReady extends HTMLElement {
  /** @type {boolean} Component connection state */
  #isConnected = false;
  
  /** @type {AbortController | null} Event listener controller */
  #abortController = null;
  
  /** @type {ResizeObserver | null} Resize observer instance */
  #resizeObserver = null;

  constructor() {
    super();
  }

  connectedCallback() {
    if (this.#isConnected) return;
    
    try {
      this.#isConnected = true;
      this.#abortController = new AbortController();
      this.#initializeAllScripts();
    } catch (error) {
      this.#handleError('Failed to initialize DOM ready scripts', error);
    }
  }

  disconnectedCallback() {
    this.#cleanup();
  }

  /**
   * Initialize all DOM-ready scripts
   * @private
   */
  #initializeAllScripts() {
    // Run all initialization scripts
    this.#handleClickOnLoadElements();
    this.#calculateFirstSectionMargin();
    this.#initializeLazyBlurImages();
    this.#adjustSliderOpacity();
  }

  /**
   * Handle elements that should be clicked on load
   * @private
   */
  #handleClickOnLoadElements() {
    try {
      const clickOnLoadElements = document.querySelectorAll(".click-on-load");
      clickOnLoadElements.forEach((element) => {
        // Add a small delay to ensure proper initialization
        requestAnimationFrame(() => {
          element.click();
        });
      });
    } catch (error) {
      this.#handleError('Failed to handle click-on-load elements', error);
    }
  }

  /**
   * Calculate and set first section margin CSS custom property
   * @private
   */
  #calculateFirstSectionMargin() {
    try {
      const firstSection = document.querySelector(
        "#MainContent .shopify-section:not(.shopify-section-group-header-group)",
      );
      
      if (!firstSection) return;
      
      const firstSectionMarginTop = parseInt(
        window.getComputedStyle(firstSection).marginTop,
      );
      
      document.body.style.setProperty(
        "--first-section-margin-top",
        `${firstSectionMarginTop}px`,
      );

      // Listen for section loads to recalculate
      document.addEventListener('shopify:section:load', () => {
        this.#calculateFirstSectionMargin();
      }, { signal: this.#abortController.signal });

    } catch (error) {
      this.#handleError('Failed to calculate first section margin', error);
    }
  }

  /**
   * Initialize lazy loading blur effect for images
   * @private
   */
  #initializeLazyBlurImages() {
    try {
      const images = document.querySelectorAll("img.js-lazyloadBlur");
      
      images.forEach((image) => {
        const placeholder = image.nextElementSibling;
        if (!placeholder) return;

        // Set up intersection observer for lazy loading
        const observer = new IntersectionObserver(
          (entries) => {
            entries.forEach((entry) => {
              if (entry.isIntersecting) {
                this.#loadBlurImage(image, placeholder);
                observer.unobserve(image);
              }
            });
          },
          {
            rootMargin: '50px',
            threshold: 0.1
          }
        );

        observer.observe(image);
      });
    } catch (error) {
      this.#handleError('Failed to initialize lazy blur images', error);
    }
  }

  /**
   * Load blur image with fade effect
   * @param {HTMLImageElement} image - Image element
   * @param {HTMLElement} placeholder - Placeholder element
   * @private
   */
  #loadBlurImage(image, placeholder) {
    try {
      image.addEventListener('load', () => {
        requestAnimationFrame(() => {
          image.style.opacity = '1';
          placeholder.style.opacity = '0';
          
          // Clean up placeholder after transition
          setTimeout(() => {
            if (placeholder.parentNode) {
              placeholder.parentNode.removeChild(placeholder);
            }
          }, 300);
        });
      }, { once: true });

      // Trigger load if not already loaded
      if (image.complete) {
        image.dispatchEvent(new Event('load'));
      }
    } catch (error) {
      this.#handleError('Failed to load blur image', error);
    }
  }

  /**
   * Adjust slider opacity based on content
   * @private
   */
  #adjustSliderOpacity() {
    try {
      const sliders = document.querySelectorAll('[data-slider-opacity]');
      
      sliders.forEach((slider) => {
        this.#setupSliderOpacityObserver(slider);
      });
    } catch (error) {
      this.#handleError('Failed to adjust slider opacity', error);
    }
  }

  /**
   * Set up resize observer for slider opacity adjustment
   * @param {HTMLElement} slider - Slider element
   * @private
   */
  #setupSliderOpacityObserver(slider) {
    try {
      if (!this.#resizeObserver) {
        this.#resizeObserver = new ResizeObserver((entries) => {
          entries.forEach((entry) => {
            if (entry.target.hasAttribute('data-slider-opacity')) {
              this.#calculateSliderOpacity(entry.target);
            }
          });
        });
      }

      this.#resizeObserver.observe(slider);
      
      // Initial calculation
      this.#calculateSliderOpacity(slider);
    } catch (error) {
      this.#handleError('Failed to setup slider opacity observer', error);
    }
  }

  /**
   * Calculate and apply slider opacity
   * @param {HTMLElement} slider - Slider element
   * @private
   */
  #calculateSliderOpacity(slider) {
    try {
      const slides = slider.querySelectorAll('.slide');
      const visibleSlides = Array.from(slides).filter(slide => {
        const rect = slide.getBoundingClientRect();
        return rect.width > 0 && rect.height > 0;
      });

      const opacity = Math.max(0.3, Math.min(1, visibleSlides.length / slides.length));
      slider.style.setProperty('--slider-opacity', opacity.toString());
    } catch (error) {
      this.#handleError('Failed to calculate slider opacity', error);
    }
  }

  /**
   * Handle errors gracefully with user feedback
   * @param {string} message - Error message
   * @param {Error} error - Original error object
   * @private
   */
  #handleError(message, error) {
    console.error(`ElectricDOMReady: ${message}`, error);
    
    this.dispatchEvent(new CustomEvent('electric:dom-ready-error', {
      detail: { message, error: error.message },
      bubbles: true
    }));
  }

  /**
   * Clean up resources and event listeners
   * @private
   */
  #cleanup() {
    this.#isConnected = false;
    
    if (this.#abortController) {
      this.#abortController.abort();
      this.#abortController = null;
    }
    
    if (this.#resizeObserver) {
      this.#resizeObserver.disconnect();
      this.#resizeObserver = null;
    }
  }
}

// Register the custom element
if (!customElements.get('electric-dom-ready')) {
  customElements.define('electric-dom-ready', ElectricDOMReady);
}
})();
(function () {
/**
 * ElectricVariantLinkConverter Web Component
 * 
 * Converts regular links with data-variant-preview attributes into 
 * ProductVariantPreview web components when connected to the DOM.
 * This ensures the conversion works even when scripts are injected
 * after the DOM is already loaded.
 * 
 * @example
 * <electric-variant-link-converter></electric-variant-link-converter>
 */
class ElectricVariantLinkConverter extends HTMLElement {
  /** @type {boolean} Component connection state */
  #isConnected = false;

  constructor() {
    super();
  }

  connectedCallback() {
    if (this.#isConnected) return;
    
    try {
      this.#isConnected = true;
      this.#convertVariantPreviewLinks();
    } catch (error) {
      this.#handleError('Failed to convert variant preview links', error);
    }
  }

  /**
   * Convert regular links with data-variant-preview to web components
   * @private
   */
  #convertVariantPreviewLinks() {
    try {
      // Find all links with data-variant-preview attribute
      const variantPreviewLinks = document.querySelectorAll('a[data-variant-preview]');
      
      if (variantPreviewLinks.length === 0) {
        return;
      }
      
      variantPreviewLinks.forEach((link, index) => {
        this.#convertSingleLink(link, index);
      });
      
      // Dispatch event to notify conversion is complete
      this.dispatchEvent(new CustomEvent('electric:variant-links-converted', {
        detail: { convertedCount: variantPreviewLinks.length },
        bubbles: true
      }));
      
    } catch (error) {
      this.#handleError('Failed during variant preview link conversion', error);
    }
  }

  /**
   * Convert a single link to ProductVariantPreview web component
   * @param {HTMLAnchorElement} link - Link element to convert
   * @param {number} index - Index for logging purposes
   * @private
   */
  #convertSingleLink(link, index) {
    try {
      // Create a new ProductVariantPreview element
      const previewElement = document.createElement('product-variant-preview');
      
      // Copy all attributes from the original link
      Array.from(link.attributes).forEach(attr => {
        previewElement.setAttribute(attr.name, attr.value);
      });
      
      // Explicitly set the href attribute
      if (link.href) {
        previewElement.setAttribute('href', link.href);
      }
      
      // Copy the content
      previewElement.innerHTML = link.innerHTML;
      
      // Replace the original link with animation frame for smooth transition
      requestAnimationFrame(() => {
        if (link.parentNode) {
          link.parentNode.replaceChild(previewElement, link);
        }
      });
      
    } catch (error) {
      this.#handleError(`Failed to convert link ${index + 1}`, error);
    }
  }

  /**
   * Handle errors gracefully with user feedback
   * @param {string} message - Error message
   * @param {Error} error - Original error object
   * @private
   */
  #handleError(message, error) {
    console.error(`ElectricVariantLinkConverter: ${message}`, error);
    
    this.dispatchEvent(new CustomEvent('electric:variant-converter-error', {
      detail: { message, error: error.message },
      bubbles: true
    }));
  }
}

// Register the custom element
if (!customElements.get('electric-variant-link-converter')) {
  customElements.define('electric-variant-link-converter', ElectricVariantLinkConverter);
}
})();
(function () {
/**
 * Footer Newsletter Form Component
 * Handles newsletter form validation and accessibility enhancements
 */
class FooterNewsletter extends HTMLElement {
  #form = null;
  #emailInput = null;
  #errorElement = null;
  #isConnected = false;

  constructor() {
    super();
    this.#bindEventHandlers();
  }

  #bindEventHandlers() {
    // No binding needed for private methods; remove assignments to avoid errors.
  }

  connectedCallback() {
    if (this.#isConnected) return;
    this.#isConnected = true;

    try {
      this.#initializeElements();
      this.#attachEventListeners();
    } catch (error) {
      console.error('FooterNewsletter: Initialization error:', error);
    }
  }

  disconnectedCallback() {
    this.#removeEventListeners();
    this.#isConnected = false;
  }

  #initializeElements() {
    this.#form = this.querySelector('#footer-newsletter-form');
    this.#emailInput = this.querySelector('#footer-subscription');
    this.#errorElement = this.querySelector('#newsletter-error');

    if (!this.#form || !this.#emailInput || !this.#errorElement) {
      throw new Error('Required form elements not found');
    }
  }

  #attachEventListeners() {
    this.#emailInput.addEventListener('input', this.#handleInput, { passive: true });
    this.#form.addEventListener('submit', this.#handleSubmit);
    this.#emailInput.addEventListener('keydown', this.#handleKeydown);
  }

  #removeEventListeners() {
    if (this.#emailInput) {
      this.#emailInput.removeEventListener('input', this.#handleInput);
      this.#emailInput.removeEventListener('keydown', this.#handleKeydown);
    }
    if (this.#form) {
      this.#form.removeEventListener('submit', this.#handleSubmit);
    }
  }

  #handleInput() {
    const isValid = this.#emailInput.checkValidity();
    this.#emailInput.setAttribute('aria-invalid', !isValid);
    
    if (isValid) {
      this.#errorElement.classList.add('hidden');
      this.#errorElement.textContent = '';
    }
  }

  #handleSubmit(e) {
    if (!this.#emailInput.checkValidity()) {
      e.preventDefault();
      this.#emailInput.setAttribute('aria-invalid', 'true');
      this.#errorElement.classList.remove('hidden');
      this.#errorElement.textContent =
        window.newsletterStrings?.errorMessage || this.#errorElement.dataset.errorMessage || this.#errorElement.textContent;
      this.#emailInput.focus();
    }
  }

  #handleKeydown(e) {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      this.#form.requestSubmit();
    }
  }

  // Static utility methods for testing and external access
  static getForm(formId = 'footer-newsletter-form') {
    return document.getElementById(formId);
  }

  static getEmailInput(inputId = 'footer-subscription') {
    return document.getElementById(inputId);
  }
}

// Register component with safety check
if (!customElements.get('footer-newsletter')) {
  customElements.define('footer-newsletter', FooterNewsletter);
}

// Module export support
if (typeof module !== 'undefined' && module.exports) {
  module.exports = FooterNewsletter;
}
})();
(function () {
/**
 * Product Card Add to Cart Component
 * 
 * Handles add-to-cart functionality for product cards with add-to-bag icons.
 * Provides loading states, error handling, and cart updates.
 * 
 * @example
 * <button data-add-to-cart-trigger data-product-id="123" data-variant-id="456">
 *   <icon>add-to-bag</icon>
 * </button>
 */
class ProductCardATC extends HTMLElement {
  /** @type {HTMLButtonElement | null} Add to cart trigger button */
  #trigger = null;
  
  /** @type {HTMLFormElement | null} Hidden product form */
  #form = null;
  
  /** @type {boolean} Component connection state */
  #isConnected = false;
  
  /** @type {AbortController | null} Event listener controller */
  #abortController = null;

  constructor() {
    super();
  }

  connectedCallback() {
    if (this.#isConnected) return;
    
    try {
      this.#isConnected = true;
      this.#initialize();
    } catch (error) {
      this.#handleError('Failed to initialize product card ATC', error);
    }
  }

  disconnectedCallback() {
    this.#cleanup();
  }

  /**
   * Initialize the component
   * @private
   */
  #initialize() {
    this.#trigger = this.querySelector('[data-add-to-cart-trigger]');
    this.#form = this.querySelector('form[data-type="add-to-cart-form"]');
    
    if (!this.#trigger || !this.#form) {
      this.#logError(new Error('Missing required elements: trigger button or form'));
      return;
    }

    this.#setupEventListeners();
  }

  /**
   * Setup event listeners for add to cart functionality
   * @private
   */
  #setupEventListeners() {
    this.#abortController = new AbortController();
    
    this.#trigger.addEventListener('click', this.#handleAddToCart.bind(this), {
      signal: this.#abortController.signal,
      passive: false
    });
    
    this.#trigger.addEventListener('keydown', this.#handleKeydown.bind(this), {
      signal: this.#abortController.signal,
      passive: false
    });
  }

  /**
   * Handle add to cart button click
   * @param {Event} event - Click event
   * @private
   */
  async #handleAddToCart(event) {
    event.preventDefault();
    
    if (this.#trigger.disabled) return;
    
    try {
      this.#setLoadingState(true);
      
      const formData = new FormData(this.#form);
      
      // Get dynamic sections for cart updates
      const dynamicSectionsNodes = document.querySelectorAll('cart-dynamic');
      const sectionIds = dynamicSectionsNodes.length
        ? [...dynamicSectionsNodes].map(n => n.dataset.sectionId)
        : [];
      
      formData.append('sections', sectionIds.join(','));
      
      const response = await fetch('/cart/add.js', {
        method: 'POST',
        headers: {
          'X-Requested-With': 'XMLHttpRequest'
        },
        body: formData
      });
      
      if (!response.ok) {
        throw new Error(`HTTP ${response.status}: ${response.statusText}`);
      }
      
      const result = await response.json();
      
      if (result.status) {
        throw new Error(result.description || 'Failed to add to cart');
      }
      
      this.#handleSuccess(result);
      
    } catch (error) {
      this.#handleError('Failed to add product to cart', error);
    } finally {
      this.#setLoadingState(false);
    }
  }

  /**
   * Handle keyboard navigation
   * @param {KeyboardEvent} event - Keydown event
   * @private
   */
  #handleKeydown(event) {
    if (event.key === 'Enter' || event.key === ' ') {
      event.preventDefault();
      this.#trigger.click();
    }
  }

  /**
   * Set loading state for the button
   * @param {boolean} isLoading - Whether to show loading state
   * @private
   */
  #setLoadingState(isLoading) {
    if (isLoading) {
      this.#trigger.setAttribute('data-state', 'loading');
      this.#trigger.setAttribute(
        'aria-label',
        window.variantStrings?.addingToCart || this.#trigger.dataset.labelLoading || 'Adding to Cart',
      );
    } else {
      this.#trigger.removeAttribute('data-state');
      this.#trigger.setAttribute(
        'aria-label',
        window.variantStrings?.addToCart || this.#trigger.dataset.labelIdle || 'Add to Cart',
      );
    }
  }

  /**
   * Handle successful add to cart
   * @param {Object} result - Cart add result
   * @private
   */
  #handleSuccess(result) {
    // Set success state on button
    this.#trigger.setAttribute('data-state', 'success');
    
    // Update dynamic sections if provided
    const dynamicSectionsNodes = document.querySelectorAll('cart-dynamic');
    if (dynamicSectionsNodes.length && result.sections) {
      [...dynamicSectionsNodes].forEach(node => {
        const htmlString = result.sections[node.dataset.sectionId];
        if (!htmlString) return;
        
        const html = new DOMParser().parseFromString(htmlString, 'text/html');
        const updated = html.querySelector(`cart-dynamic[data-section-id='${node.dataset.sectionId}']`);
        if (!updated) return;
        
        node.innerHTML = updated.innerHTML;
        node.className = updated.className;
        
        // Call lifecycle hook if present
        if (typeof node.modified === 'function') {
          node.modified();
        }
      });
    }
    
    // Dispatch cart update event
    document.dispatchEvent(new CustomEvent('cart:update', {
      detail: {
        items: [result],
        source: 'product-card-atc'
      },
      bubbles: true
    }));

    // Haptic feedback if available
    navigator.vibrate?.(100);

    // On cart page, do not open cart drawer again.
    if (!document.body.classList.contains('template-cart')) {
      document.dispatchEvent(new CustomEvent('cart:added', {
        bubbles: true,
        detail: {
          source: 'product-card-atc',
          product: result
        }
      }));
    }
  }

  /**
   * Show notification in top-right corner
   * @param {string} message - Notification message
   * @private
   */
  #showNotification(message) {
    // Remove existing notification if present
    const existingNotification = document.querySelector('.product-card-notification');
    if (existingNotification) {
      existingNotification.remove();
    }
    
    // Create notification element
    const notification = document.createElement('div');
    notification.className = 'product-card-notification fixed top-4 right-4 z-[9999] color-schema-contrast bg-surface px-4 py-2 shadow-lg transform translate-x-full transition-transform duration-300 ease-out';
    notification.textContent = message;
    
    // Add to document
    document.body.appendChild(notification);
    
    // Animate in
    requestAnimationFrame(() => {
      notification.classList.remove('translate-x-full');
    });
    
    // Remove after delay
    setTimeout(() => {
      notification.classList.add('translate-x-full');
      setTimeout(() => {
        if (notification.parentNode) {
          notification.remove();
        }
      }, 300);
    }, 2000);
  }

  /**
   * Handle errors gracefully
   * @param {string} message - Error message
   * @param {Error} error - Original error
   * @private
   */
  #handleError(message, error) {
    console.error(`ProductCardATC: ${message}`, error);
    
    // Set error state on button
    this.#trigger.setAttribute('data-state', 'error');
    
    // Haptic feedback for error
    navigator.vibrate?.(200);
    
    // Remove error state after delay
    setTimeout(() => {
      this.#trigger.removeAttribute('data-state');
    }, 3000);
  }

  /**
   * Log errors for debugging
   * @param {Error} error - Error to log
   * @private
   */
  #logError(error) {
    console.error('ProductCardATC:', error);
  }

  /**
   * Clean up resources and event listeners
   * @private
   */
  #cleanup() {
    this.#isConnected = false;
    this.#abortController?.abort();
    this.#abortController = null;
  }
}

// Register the component
customElements.define('product-card-atc', ProductCardATC);
})();
(function () {
/**
 * Debug logging function that only logs if 'debug_quick_atc=1' is present in URL query parameters
 * @param {...any} args - Arguments to log
 */
function debugLog(...args) {
  const urlParams = new URLSearchParams(window.location.search);
  if (urlParams.get('debug_quick_atc') === '1') {
    console.log(...args);
  }
}

/**
 * QuickATCTrigger Web Component
 *
 * Custom element that automatically initializes when connected to DOM.
 * Manages quick add-to-cart modal functionality with product content preloading
 * and dynamic section fetching.
 *
 * Features:
 * - Auto-initialization via connectedCallback
 * - Hover-based content preloading
 * - Modal state management
 * - Automatic cleanup on disconnect
 *
 * @example
 * // HTML markup (auto-initialized when added to DOM):
 * <quick-atc-trigger data-modal-id="quick-atc-modal-123"
 *                    data-product-handle="product-handle"
 *                    data-product-url="/products/handle?section_id=product--main">
 *   <button data-modal-trigger="quick-atc-modal-123">Quick Add</button>
 * </quick-atc-trigger>
 */
class QuickATCTrigger extends HTMLElement {
  #modalId = '';
  #productHandle = '';
  #productUrl = '';
  #isInitialized = false;
  #preloadedHTML = null;
  #isLoading = false;
  #abortController = null;
  #modalOpenHandler = null;
  #modalCloseHandler = null;
  #hoverHandler = null;

  /**
   * Web Component lifecycle - called when element is added to DOM
   */
  connectedCallback() {
    debugLog('🔌 QuickATCTrigger: Connected to DOM');
    this.#init();
  }

  /**
   * Web Component lifecycle - called when element is removed from DOM
   */
  disconnectedCallback() {
    debugLog('🔌 QuickATCTrigger: Disconnected from DOM');
    this.#cleanup();
  }

  /**
   * Initialize the component
   * @private
   */
  #init() {
    if (this.#isInitialized) return;

    // Extract data from attributes
    this.#modalId = this.dataset.modalId || '';
    this.#productHandle = this.dataset.productHandle || '';
    this.#productUrl = this.dataset.productUrl || '';

    if (!this.#modalId || !this.#productHandle || !this.#productUrl) {
      console.warn('⚠️ QuickATCTrigger: Missing required data attributes', {
        modalId: this.#modalId,
        productHandle: this.#productHandle,
        productUrl: this.#productUrl
      });
      return;
    }

    // Ensure product URL has section_id parameter for product--quick-atc section
    this.#productUrl = this.#ensureSectionId(this.#productUrl);

    debugLog('✅ QuickATCTrigger: Initializing', {
      modalId: this.#modalId,
      productHandle: this.#productHandle,
      productUrl: this.#productUrl
    });

    try {
      this.#setupEventListeners();
      this.#addHoverListeners();
      this.#isInitialized = true;
    } catch (error) {
      this.#handleError('Failed to initialize QuickATCTrigger', error);
    }
  }

  /**
   * Ensure the URL has the section_id parameter for product--quick-atc
   * @param {string} url - The product URL
   * @returns {string} URL with section_id parameter
   * @private
   */
  #ensureSectionId(url) {
    try {
      // Parse the URL
      const urlObj = new URL(url, window.location.origin);

      // Check if section_id is already present
      if (!urlObj.searchParams.has('section_id')) {
        // Add section_id=product--quick-atc parameter
        urlObj.searchParams.set('section_id', 'product--quick-atc');
      }

      // Return the pathname + search params (relative URL)
      return urlObj.pathname + urlObj.search;
    } catch (error) {
      console.warn('⚠️ QuickATCTrigger: Failed to parse URL, using as-is', error);
      // Fallback: append section_id if not present
      const separator = url.includes('?') ? '&' : '?';
      return url.includes('section_id') ? url : `${url}${separator}section_id=product--quick-atc`;
    }
  }

  /**
   * Setup event listeners for modal operations
   * @private
   */
  #setupEventListeners() {
    // Create bound handlers for cleanup
    this.#modalOpenHandler = this.#handleModalOpen.bind(this);
    this.#modalCloseHandler = this.#handleModalClose.bind(this);

    // Listen for modal events
    document.addEventListener('modal:open', this.#modalOpenHandler, { passive: true });
    document.addEventListener('modal:close', this.#modalCloseHandler, { passive: true });
  }

  /**
   * Add hover listener for content preloading
   * @private
   */
  #addHoverListeners() {
    // Find parent product card or use self as hover target
    const hoverTarget = this.closest('.product-card') || this;

    this.#hoverHandler = () => {
      if (!this.#preloadedHTML && !this.#isLoading) {
        this.#preloadContent();
      }
    };

    hoverTarget.addEventListener('mouseenter', this.#hoverHandler, { once: true, passive: true });
  }

  /**
   * Preload product content for faster modal display
   * @private
   */
  async #preloadContent() {
    if (this.#isLoading || this.#preloadedHTML) return;

    debugLog('🔄 QuickATCTrigger: Preloading content', { modalId: this.#modalId });
    this.#isLoading = true;

    try {
      this.#abortController = new AbortController();

      const response = await fetch(this.#productUrl, {
        signal: this.#abortController.signal,
        headers: {
          'Accept': 'text/html',
          'X-Requested-With': 'XMLHttpRequest'
        }
      });

      if (!response.ok) {
        throw new Error(`Failed to fetch: ${response.status} ${response.statusText}`);
      }

      const html = await response.text();
      this.#preloadedHTML = html;
      this.#isLoading = false;

      debugLog('✅ QuickATCTrigger: Preload successful', { modalId: this.#modalId });

      // Dispatch preload success event
      this.dispatchEvent(new CustomEvent('quick-atc:preload-success', {
        detail: { productHandle: this.#productHandle, modalId: this.#modalId },
        bubbles: true
      }));

    } catch (error) {
      this.#isLoading = false;
      if (error.name !== 'AbortError') {
        console.error('❌ QuickATCTrigger: Preload failed', error);
        this.#preloadedHTML = null;
      }
    }
  }

  /**
   * Handle modal open events
   * @private
   */
  async #handleModalOpen(event) {
    const { modalId } = event.detail || {};
    if (modalId !== this.#modalId) return;

    debugLog('🚀 QuickATCTrigger: Handling modal open', { modalId: this.#modalId });

    // Show loading state when modal opens
    this.#setLoadingState(true);

    try {
      // Wait for modal DOM
      await new Promise(resolve => setTimeout(resolve, 50));

      const modalElements = this.#getModalElements();
      if (!modalElements) {
        console.error('❌ QuickATCTrigger: Modal elements not found');
        this.#setLoadingState(false);
        return;
      }

      const { loadingEl, errorEl, productEl } = modalElements;

      if (this.#preloadedHTML) {
        debugLog('✅ QuickATCTrigger: Using preloaded content');
        this.#displayContent(productEl, this.#preloadedHTML);
        this.#setLoadingState(false);
      } else {
        debugLog('🔄 QuickATCTrigger: Fetching content...');
        // Don't hide content if we don't have loading elements
        if (loadingEl) {
          this.#showState(loadingEl);
        }
        await this.#fetchAndDisplayContent(productEl, errorEl);
        this.#setLoadingState(false);
      }
    } catch (error) {
      this.#handleError('Failed to handle modal open', error);
      this.#setLoadingState(false);
    }
  }

  /**
   * Handle modal close events
   * @private
   */
  #handleModalClose(event) {
    const { modalId } = event.detail || {};
    if (modalId === this.#modalId) {
      if (this.#abortController) {
        this.#abortController.abort();
        this.#abortController = null;
      }
      // Reset loading state when modal closes
      this.#setLoadingState(false);
    }
  }

  /**
   * Set loading state for the trigger button
   * @param {boolean} isLoading - Whether to show loading state
   * @private
   */
  #setLoadingState(isLoading) {
    const triggerButton = this.querySelector('[data-quick-atc-trigger]') ||
                         document.querySelector(`[data-quick-atc-trigger="${this.#modalId}"]`);
    if (!triggerButton) return;

    const iconElement = triggerButton.querySelector('[data-icon]');
    const spinnerElement = triggerButton.querySelector('[data-spinner]');

    if (isLoading) {
      triggerButton.disabled = true;
      triggerButton.setAttribute(
        'aria-label',
        window.variantStrings?.loadingProduct ||
          triggerButton.dataset.labelLoading ||
          'Loading product...',
      );
      if (iconElement) iconElement.classList.add('opacity-0');
      if (spinnerElement) spinnerElement.classList.remove('hidden');
    } else {
      triggerButton.disabled = false;
      const productTitle = this.#productHandle || 'product';
      const template =
        window.variantStrings?.quickAddWithTitle ||
        triggerButton.dataset.labelIdleTemplate ||
        'Quick add [title] to cart';
      triggerButton.setAttribute('aria-label', template.replaceAll('[title]', productTitle));
      if (iconElement) iconElement.classList.remove('opacity-0');
      if (spinnerElement) spinnerElement.classList.add('hidden');
    }
  }

  /**
   * Get modal DOM elements
   * @private
   */
  #getModalElements() {
    const modal = document.getElementById('electric-modal-container');
    if (!modal) return null;

    const modalContent = modal.querySelector('.electric-modal-content');
    if (!modalContent) return null;

    // Look for the section-fetcher element which contains the actual content
    const sectionFetcher = modalContent.querySelector('section-fetcher');

    return {
      loadingEl: modalContent.querySelector('.quick-atc-loading'),
      errorEl: modalContent.querySelector('.quick-atc-error'),
      productEl: sectionFetcher || modalContent.querySelector('.quick-atc-product-content')
    };
  }

  /**
   * Fetch and display product content
   * @private
   */
  async #fetchAndDisplayContent(productEl, errorEl) {
    try {
      this.#abortController = new AbortController();

      const response = await fetch(this.#productUrl, {
        signal: this.#abortController.signal,
        headers: {
          'Accept': 'text/html',
          'X-Requested-With': 'XMLHttpRequest'
        }
      });

      if (!response.ok) {
        throw new Error(`Failed to fetch: ${response.status} ${response.statusText}`);
      }

      const html = await response.text();
      this.#preloadedHTML = html;
      this.#displayContent(productEl, html);

    } catch (error) {
      if (error.name !== 'AbortError') {
        console.error('❌ QuickATCTrigger: Fetch failed', error);
        this.#showState(errorEl);
      }
    }
  }

  /**
   * Display product content in modal
   * @private
   */
  #displayContent(productEl, html) {
    if (!productEl) return;

    try {
      productEl.innerHTML = html;

      // Reinitialize Shopify scripts
      if (window.Shopify?.PaymentButton?.init) {
        window.Shopify.PaymentButton.init();
      }
      if (window.ProductForm?.init) {
        window.ProductForm.init();
      }

      // Dispatch content loaded event
      this.dispatchEvent(new CustomEvent('quick-atc:content-loaded', {
        detail: { productHandle: this.#productHandle, modalId: this.#modalId },
        bubbles: true
      }));

      // Only show state if we have loading/error elements to manage
      const modal = document.getElementById('electric-modal-container');
      if (modal?.querySelector('.quick-atc-loading')) {
        this.#showState(productEl);
      }

    } catch (error) {
      console.error('❌ QuickATCTrigger: Failed to display content', error);
    }
  }

  /**
   * Show specific modal state
   * @private
   */
  #showState(elementToShow) {
    const modalElements = this.#getModalElements();
    if (!modalElements) return;

    const { loadingEl, errorEl, productEl } = modalElements;

    // Hide all states
    [loadingEl, errorEl, productEl].forEach(el => {
      if (el) el.classList.add('hidden');
    });

    // Show specified state
    if (elementToShow) {
      elementToShow.classList.remove('hidden');
    }
  }

  /**
   * Handle errors
   * @private
   */
  #handleError(message, error) {
    console.error(`QuickATCTrigger [${this.#modalId}]: ${message}`, error);

    this.dispatchEvent(new CustomEvent('quick-atc:error', {
      detail: {
        message,
        error: error.message,
        modalId: this.#modalId,
        productHandle: this.#productHandle
      },
      bubbles: true
    }));
  }

  /**
   * Cleanup resources
   * @private
   */
  #cleanup() {
    // Abort any ongoing requests
    if (this.#abortController) {
      this.#abortController.abort();
      this.#abortController = null;
    }

    // Remove event listeners
    if (this.#modalOpenHandler) {
      document.removeEventListener('modal:open', this.#modalOpenHandler);
    }
    if (this.#modalCloseHandler) {
      document.removeEventListener('modal:close', this.#modalCloseHandler);
    }

    // Remove hover listener
    if (this.#hoverHandler) {
      const hoverTarget = this.closest('.product-card') || this;
      hoverTarget.removeEventListener('mouseenter', this.#hoverHandler);
    }

    // Clear state
    this.#isInitialized = false;
    this.#preloadedHTML = null;
  }
}

// Register the custom element
customElements.define('quick-atc-trigger', QuickATCTrigger);

/**
 * Legacy support - convert existing buttons to web components
 * This observer watches for dynamically added elements with data-quick-atc-trigger
 * and wraps them in the custom element
 */
class QuickATCLegacyAdapter {
  #observer = null;
  #processedElements = new WeakSet();

  constructor() {
    this.#init();
  }

  #init() {
    // Process existing elements
    this.#processExistingElements();

    // Setup mutation observer for dynamic content
    this.#setupObserver();
  }

  #processExistingElements() {
    const triggers = document.querySelectorAll('[data-quick-atc-trigger]:not(quick-atc-trigger [data-quick-atc-trigger])');
    triggers.forEach(trigger => this.#wrapTrigger(trigger));
  }

  #wrapTrigger(trigger) {
    // Skip if already processed
    if (this.#processedElements.has(trigger)) return;
    if (trigger.closest('quick-atc-trigger')) return;

    const modalId = trigger.dataset.quickAtcTrigger;
    const productHandle = trigger.dataset.modalProductHandle;
    let productUrl = trigger.dataset.modalProductUrl;

    if (!modalId || !productHandle || !productUrl) return;

    debugLog('🔄 QuickATCLegacyAdapter: Converting legacy trigger', { modalId, productUrl });

    // Create web component wrapper
    const wrapper = document.createElement('quick-atc-trigger');
    wrapper.dataset.modalId = modalId;
    wrapper.dataset.productHandle = productHandle;
    wrapper.dataset.productUrl = productUrl;

    // Wrap the trigger element
    trigger.parentNode.insertBefore(wrapper, trigger);
    wrapper.appendChild(trigger);

    this.#processedElements.add(trigger);
  }

  #setupObserver() {
    this.#observer = new MutationObserver(mutations => {
      for (const mutation of mutations) {
        if (mutation.type === 'childList') {
          mutation.addedNodes.forEach(node => {
            if (node.nodeType === Node.ELEMENT_NODE) {
              // Check if the node itself is a trigger
              if (node.matches?.('[data-quick-atc-trigger]:not(quick-atc-trigger [data-quick-atc-trigger])')) {
                this.#wrapTrigger(node);
              }

              // Check for triggers within the node
              const triggers = node.querySelectorAll?.('[data-quick-atc-trigger]:not(quick-atc-trigger [data-quick-atc-trigger])');
              triggers?.forEach(trigger => this.#wrapTrigger(trigger));
            }
          });
        }
      }
    });

    this.#observer.observe(document.body, {
      childList: true,
      subtree: true
    });
  }

  destroy() {
    if (this.#observer) {
      this.#observer.disconnect();
      this.#observer = null;
    }
  }
}

// Initialize legacy adapter when DOM is ready
if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', () => {
    window.quickATCLegacyAdapter = new QuickATCLegacyAdapter();
  });
} else {
  window.quickATCLegacyAdapter = new QuickATCLegacyAdapter();
}

// Global handler for quick-atc button clicks to show spinner
document.addEventListener('click', (event) => {
  const button = event.target.closest('[data-quick-atc-trigger]');
  if (button) {
    const iconElement = button.querySelector('[data-icon]');
    const spinnerElement = button.querySelector('[data-spinner]');

    if (iconElement && spinnerElement) {
      iconElement.classList.add('opacity-0');
      spinnerElement.classList.remove('hidden');
    }
  }
}, true);

// Export for module systems
if (typeof module !== 'undefined' && module.exports) {
  module.exports = { QuickATCTrigger, QuickATCLegacyAdapter };
}
})();
(function () {
/**
 * FindMyCar Web Component
 * 
 * A lightweight search component for finding car types (collections).
 * Provides predictive search with debounced input handling and dropdown results.
 * 
 * @example
 * <find-my-car>
 *   <input type="text" placeholder="Search your car">
 * </find-my-car>
 */
class FindMyCar extends HTMLElement {
  /** @type {HTMLInputElement | null} Search input element */
  #input = null;

  /** @type {HTMLDivElement | null} Results dropdown container */
  #dropdown = null;

  /** @type {Object<string, string>} Cached search results */
  #cachedResults = {};

  /** @type {number | null} Debounce timer ID */
  #debounceTimer = null;

  /** @type {number} Debounce delay in milliseconds */
  #debounceDelay = 300;

  /** @type {AbortController | null} Fetch abort controller */
  #abortController = null;

  /** @type {boolean} Component connection state */
  #isConnected = false;

  /** @type {boolean} Whether dropdown is currently open */
  #isOpen = false;

  constructor() {
    super();
  }

  connectedCallback() {
    if (this.#isConnected) return;

    try {
      this.#isConnected = true;
      this.#initialize();
    } catch (error) {
      this.#handleError('Failed to initialize find-my-car component', error);
    }
  }

  disconnectedCallback() {
    this.#cleanup();
  }

  /**
   * Initialize the component
   * @private
   */
  #initialize() {
    this.#input = this.querySelector('input[type="text"]');
    
    if (!this.#input) {
      throw new Error('FindMyCar requires an input[type="text"] element');
    }

    this.#createDropdown();
    this.#setupEventListeners();
  }

  /**
   * Create the dropdown element
   * @private
   */
  #createDropdown() {
    this.#dropdown = document.createElement('div');
    this.#dropdown.className = 'find-my-car__dropdown absolute bg-white top-full -left-px -right-px pt-2 max-h-80 overflow-y-auto hidden peer-focus:border-secondary border-transparent border-x border-b rounded-b-4xl';
    this.#dropdown.setAttribute('role', 'listbox');
    this.#dropdown.setAttribute(
      'aria-label',
      this.dataset.resultsAria ||
        window.findYourCarStrings?.resultsAria ||
        'Car search results',
    );
    
    // Make the container relative for absolute positioning
    this.classList.add('relative', 'block');
    
    this.appendChild(this.#dropdown);
  }

  /**
   * Setup event listeners
   * @private
   */
  #setupEventListeners() {
    // Input events
    this.#input.addEventListener('input', this.#handleInput.bind(this));
    this.#input.addEventListener('focus', this.#handleFocus.bind(this));
    this.#input.addEventListener('keydown', this.#handleKeydown.bind(this));
    
    // Click outside to close
    document.addEventListener('click', this.#handleClickOutside.bind(this));
    
    // Escape key to close
    document.addEventListener('keydown', this.#handleEscape.bind(this));
  }

  /**
   * Handle input events
   * @param {Event} event - Input event
   * @private
   */
  #handleInput(event) {
    const query = this.#getQuery();
    
    // Clear debounce timer
    clearTimeout(this.#debounceTimer);
    
    // If empty, close dropdown
    if (!query) {
      this.#closeDropdown();
      return;
    }
    
    // Debounce the search
    this.#debounceTimer = setTimeout(() => {
      this.#performSearch(query);
    }, this.#debounceDelay);
  }

  /**
   * Handle focus events
   * @param {Event} event - Focus event
   * @private
   */
  #handleFocus(event) {
    const query = this.#getQuery();
    
    // If we have a query and cached results, show them
    if (query && this.#cachedResults[query.toLowerCase()]) {
      this.#renderResults(this.#cachedResults[query.toLowerCase()]);
      this.#openDropdown();
    }
  }

  /**
   * Handle keyboard navigation
   * @param {KeyboardEvent} event - Keyboard event
   * @private
   */
  #handleKeydown(event) {
    if (!this.#isOpen) return;

    switch (event.key) {
      case 'ArrowDown':
        event.preventDefault();
        this.#focusNextItem();
        break;
      case 'ArrowUp':
        event.preventDefault();
        this.#focusPreviousItem();
        break;
      case 'Enter':
        event.preventDefault();
        this.#selectFocusedItem();
        break;
      case 'Escape':
        event.preventDefault();
        this.#closeDropdown();
        this.#input.blur();
        break;
    }
  }

  /**
   * Handle click outside component
   * @param {MouseEvent} event - Click event
   * @private
   */
  #handleClickOutside(event) {
    if (!this.contains(event.target)) {
      this.#closeDropdown();
    }
  }

  /**
   * Handle escape key globally
   * @param {KeyboardEvent} event - Keyboard event
   * @private
   */
  #handleEscape(event) {
    if (event.key === 'Escape' && this.#isOpen) {
      this.#closeDropdown();
    }
  }

  /**
   * Get trimmed query from input
   * @returns {string} Trimmed query
   * @private
   */
  #getQuery() {
    return this.#input?.value.trim() || '';
  }

  /**
   * Perform search for collections
   * @param {string} query - Search query
   * @private
   */
  async #performSearch(query) {
    const queryKey = query.toLowerCase();
    
    // Check cache first
    if (this.#cachedResults[queryKey]) {
      this.#renderResults(this.#cachedResults[queryKey]);
      this.#openDropdown();
      return;
    }

    // Show loading state
    this.#showLoading();

    // Abort previous request if exists
    if (this.#abortController) {
      this.#abortController.abort();
    }

    this.#abortController = new AbortController();

    try {
      const response = await fetch(
        `${routes.predictive_search_url}?q=${encodeURIComponent(query)}&resources[type]=collection&section_id=api--search--predictive`,
        { signal: this.#abortController.signal }
      );

      if (!response.ok) {
        throw new Error(`HTTP ${response.status}: ${response.statusText}`);
      }

      const html = await response.text();
      const parser = new DOMParser();
      const doc = parser.parseFromString(html, 'text/html');
      const section = doc.querySelector('#shopify-section-api--search--predictive');

      if (section) {
        // Extract collections from the response
        const collections = this.#extractCollections(section);
        
        // Cache the results
        this.#cachedResults[queryKey] = collections;
        
        // Render results
        this.#renderResults(collections);
        this.#openDropdown();
      } else {
        this.#showNoResults();
      }
    } catch (error) {
      if (error.name === 'AbortError') {
        // Request was aborted, ignore
        return;
      }
      
      this.#handleError('Failed to fetch search results', error);
      this.#showError();
    }
  }

  /**
   * Extract collections from search results
   * @param {HTMLElement} section - Section element containing results
   * @returns {Array<{title: string, url: string, handle: string}>} Array of collection objects
   * @private
   */
  #extractCollections(section) {
    const collections = [];
    const collectionElements = section.querySelectorAll('[data-collection-handle]');
    
    collectionElements.forEach(element => {
      const title = element.querySelector('[data-collection-title]')?.textContent?.trim() || 
                   element.textContent?.trim();
      const url = element.getAttribute('href') || 
                 element.querySelector('a')?.getAttribute('href');
      const handle = element.getAttribute('data-collection-handle');
      
      if (title && url && handle) {
        collections.push({ title, url, handle });
      }
    });

    // Fallback: try to find any links with collection URLs
    if (collections.length === 0) {
      const links = section.querySelectorAll('a[href*="/collections/"]');
      links.forEach(link => {
        const url = link.getAttribute('href');
        const title = link.textContent?.trim();
        const handle = url?.split('/collections/')[1]?.split('?')[0];
        
        if (title && url && handle) {
          collections.push({ title, url, handle });
        }
      });
    }

    return collections;
  }

  /**
   * Render search results in dropdown
   * @param {Array<{title: string, url: string, handle: string}>} collections - Collections to render
   * @private
   */
  #renderResults(collections) {
    if (!collections || collections.length === 0) {
      this.#showNoResults();
      return;
    }

    const html = collections.map((collection, index) => `
      <a
        href="${this.#escapeHtml(collection.url)}"
        class="block px-6 py-4 hover:bg-surface transition-colors duration-150 focus:bg-gray-100 focus:outline-none"
        role="option"
        data-collection-handle="${this.#escapeHtml(collection.handle)}"
        tabindex="-1"
      >
        <div class="font-medium text-primary">${this.#escapeHtml(collection.title)}</div>
      </a>
    `).join('');

    this.#dropdown.innerHTML = html;
    
    // Setup click handlers for collection items
    this.#dropdown.querySelectorAll('a').forEach(link => {
      link.addEventListener('click', (e) => {
        this.#handleCollectionSelect(e, link);
      });
    });
  }

  /**
   * Handle collection selection
   * @param {Event} event - Click event
   * @param {HTMLElement} link - Selected link element
   * @private
   */
  #handleCollectionSelect(event, link) {
    const collectionHandle = link.getAttribute('data-collection-handle');
    const collectionTitle = link.textContent.trim();
    
    // Dispatch custom event
    this.dispatchEvent(new CustomEvent('collection:selected', {
      detail: {
        handle: collectionHandle,
        title: collectionTitle,
        url: link.getAttribute('href')
      },
      bubbles: true
    }));
    
    // Update input with selected value
    this.#input.value = collectionTitle;
    
    // Close dropdown
    this.#closeDropdown();
  }

  /**
   * Show loading state
   * @private
   */
  #showLoading() {
    const searching =
      this.dataset.searching || window.findYourCarStrings?.searching || 'Searching...';
    this.#dropdown.innerHTML = `
      <div class="px-6 py-8 text-center text-secondary">
        <div class="inline-block w-5 h-5 border-2 border-primary-sub border-t-transparent rounded-full animate-spin"></div>
        <div class="mt-2">${searching}</div>
      </div>
    `;
    this.#openDropdown();
  }

  /**
   * Show no results message
   * @private
   */
  #showNoResults() {
    const noResults =
      this.dataset.noResults ||
      window.findYourCarStrings?.noResults ||
      'No cars found matching your search';
    this.#dropdown.innerHTML = `
      <div class="px-6 py-8 text-center text-secondary">
        <div class="text-lg mb-2">🔍</div>
        <div>${noResults}</div>
      </div>
    `;
    this.#openDropdown();
  }

  /**
   * Show error message
   * @private
   */
  #showError() {
    const loadError =
      this.dataset.loadError ||
      window.findYourCarStrings?.loadError ||
      'Failed to load results. Please try again.';
    this.#dropdown.innerHTML = `
      <div class="px-6 py-8 text-center text-system-red">
        <div class="text-lg mb-2">⚠️</div>
        <div>${loadError}</div>
      </div>
    `;
    this.#openDropdown();
  }

  /**
   * Open dropdown
   * @private
   */
  #openDropdown() {
    if (this.#isOpen) return;
    
    this.#dropdown.classList.remove('hidden');
    this.classList.add('is-open');
    this.#isOpen = true;
    this.#input.setAttribute('aria-expanded', 'true');
    
    // Dispatch opened event
    this.dispatchEvent(new CustomEvent('dropdown:opened', {
      bubbles: true
    }));
  }

  /**
   * Close dropdown
   * @private
   */
  #closeDropdown() {
    if (!this.#isOpen) return;
    
    this.#dropdown.classList.add('hidden');
    this.classList.remove('is-open');
    this.#isOpen = false;
    this.#input.setAttribute('aria-expanded', 'false');
    
    // Dispatch closed event
    this.dispatchEvent(new CustomEvent('dropdown:closed', {
      bubbles: true
    }));
  }

  /**
   * Focus next item in dropdown
   * @private
   */
  #focusNextItem() {
    const items = Array.from(this.#dropdown.querySelectorAll('a'));
    const currentIndex = items.findIndex(item => item === document.activeElement);
    
    if (currentIndex === -1) {
      // Focus first item
      items[0]?.focus();
    } else if (currentIndex < items.length - 1) {
      // Focus next item
      items[currentIndex + 1]?.focus();
    } else {
      // Loop back to input
      this.#input?.focus();
    }
  }

  /**
   * Focus previous item in dropdown
   * @private
   */
  #focusPreviousItem() {
    const items = Array.from(this.#dropdown.querySelectorAll('a'));
    const currentIndex = items.findIndex(item => item === document.activeElement);
    
    if (currentIndex === -1 || currentIndex === 0) {
      // Focus input
      this.#input?.focus();
    } else {
      // Focus previous item
      items[currentIndex - 1]?.focus();
    }
  }

  /**
   * Select currently focused item
   * @private
   */
  #selectFocusedItem() {
    const focusedItem = this.#dropdown.querySelector('a:focus');
    
    if (focusedItem) {
      focusedItem.click();
    } else {
      // If no item is focused, close dropdown
      this.#closeDropdown();
    }
  }

  /**
   * Escape HTML to prevent XSS
   * @param {string} text - Text to escape
   * @returns {string} Escaped HTML
   * @private
   */
  #escapeHtml(text) {
    const div = document.createElement('div');
    div.textContent = text;
    return div.innerHTML;
  }

  /**
   * Handle errors gracefully
   * @param {string} message - Error message
   * @param {Error} error - Original error object
   * @private
   */
  #handleError(message, error) {
    console.error(`FindMyCar: ${message}`, error);
    
    this.dispatchEvent(new CustomEvent('search:error', {
      detail: { message, error: error.message },
      bubbles: true
    }));
  }

  /**
   * Clean up resources and event listeners
   * @private
   */
  #cleanup() {
    this.#isConnected = false;
    
    // Clear timers
    clearTimeout(this.#debounceTimer);
    this.#debounceTimer = null;
    
    // Abort pending requests
    if (this.#abortController) {
      this.#abortController.abort();
      this.#abortController = null;
    }
    
    // Remove event listeners
    document.removeEventListener('click', this.#handleClickOutside);
    document.removeEventListener('keydown', this.#handleEscape);
    
    // Clear cache
    this.#cachedResults = {};
  }
}

// Register the custom element
customElements.define('find-my-car', FindMyCar);
})();
(function () {
/**
 * SelectedCarStore
 *
 * Singleton for persisting and broadcasting the customer's selected car
 * (brand + model) across the site. Backed by localStorage so the choice
 * survives reloads, tab switches and navigations within the same origin.
 *
 * Exposed as `window.VoldtSelectedCar` and as a named export if this file
 * is ever consumed as an ES module.
 *
 * @example Read current selection
 *   const current = window.VoldtSelectedCar.get();
 *   if (current?.model?.handle === 'model-y') { ... }
 *
 * @example Save selection
 *   window.VoldtSelectedCar.set({
 *     brand: { handle: 'tesla', title: 'Tesla', logoUrl: '…' },
 *     model: { handle: 'model-y', title: 'Model Y', url: '/collections/model-y' }
 *   });
 *
 * @example Subscribe to changes
 *   const unsubscribe = window.VoldtSelectedCar.subscribe((state) => {
 *     // Re-render UI based on new state (null when cleared)
 *   });
 *
 * @event voldt:car:changed
 *   Dispatched on `document` whenever the stored selection changes. The
 *   event `detail` mirrors the payload returned by `get()`.
 */

(() => {
  'use strict';

  /** @typedef {{ handle: string, title: string, logoUrl?: string|null }} BrandRef */
  /** @typedef {{ handle: string, title: string, url: string }} ModelRef */
  /** @typedef {{ brand: BrandRef, model: ModelRef, savedAt: string }} SelectedCarState */

  const STORAGE_KEY = 'voldt:selected-car';
  const EVENT_NAME = 'voldt:car:changed';
  const SCHEMA_VERSION = 1;

  /** @type {Set<(state: SelectedCarState|null) => void>} */
  const listeners = new Set();

  /**
   * Safely read and parse the stored payload.
   * Returns null for any malformed / stale data so callers never crash.
   * @returns {SelectedCarState|null}
   */
  function read() {
    try {
      const raw = localStorage.getItem(STORAGE_KEY);
      if (!raw) return null;
      const parsed = JSON.parse(raw);
      if (!parsed || parsed.v !== SCHEMA_VERSION) return null;
      if (!parsed.brand?.handle || !parsed.model?.handle) return null;
      return {
        brand: parsed.brand,
        model: parsed.model,
        savedAt: parsed.savedAt,
      };
    } catch (error) {
      console.warn('[SelectedCarStore] Failed to read selection', error);
      return null;
    }
  }

  /**
   * Persist a state payload to localStorage.
   * @param {SelectedCarState|null} state
   */
  function write(state) {
    try {
      if (state === null) {
        localStorage.removeItem(STORAGE_KEY);
        return;
      }
      localStorage.setItem(
        STORAGE_KEY,
        JSON.stringify({ v: SCHEMA_VERSION, ...state })
      );
    } catch (error) {
      console.warn('[SelectedCarStore] Failed to persist selection', error);
    }
  }

  /**
   * Broadcast a state change to subscribers and as a DOM CustomEvent.
   * @param {SelectedCarState|null} state
   */
  function broadcast(state) {
    document.dispatchEvent(
      new CustomEvent(EVENT_NAME, {
        detail: state,
        bubbles: false,
      })
    );
    listeners.forEach((fn) => {
      try {
        fn(state);
      } catch (error) {
        console.error('[SelectedCarStore] Listener threw', error);
      }
    });
  }

  /**
   * @param {{ brand: BrandRef, model: ModelRef }} payload
   * @returns {SelectedCarState}
   */
  function set(payload) {
    if (!payload?.brand?.handle || !payload?.model?.handle) {
      throw new Error(
        '[SelectedCarStore] set() requires both brand.handle and model.handle'
      );
    }
    const state = {
      brand: {
        handle: String(payload.brand.handle),
        title: String(payload.brand.title ?? ''),
        logoUrl: payload.brand.logoUrl ?? null,
      },
      model: {
        handle: String(payload.model.handle),
        title: String(payload.model.title ?? ''),
        url: String(payload.model.url ?? ''),
      },
      savedAt: new Date().toISOString(),
    };
    write(state);
    broadcast(state);
    return state;
  }

  function clear() {
    write(null);
    broadcast(null);
  }

  /**
   * @param {(state: SelectedCarState|null) => void} fn
   * @returns {() => void} unsubscribe
   */
  function subscribe(fn) {
    if (typeof fn !== 'function') {
      throw new Error('[SelectedCarStore] subscribe() expects a function');
    }
    listeners.add(fn);
    return () => listeners.delete(fn);
  }

  // Keep multiple tabs in sync.
  window.addEventListener('storage', (event) => {
    if (event.key !== STORAGE_KEY) return;
    broadcast(read());
  });

  /** Public API */
  const api = Object.freeze({
    STORAGE_KEY,
    EVENT_NAME,
    get: read,
    set,
    clear,
    subscribe,
  });

  // Expose globally so Liquid-rendered components and inline scripts can
  // consume it without needing to import anything.
  window.VoldtSelectedCar = api;
})();
})();
(function () {
/**
 * CarMatchProductCache
 *
 * Fetches all product IDs for the customer's selected car-model collection via
 * the storefront JSON endpoint `/collections/{handle}/products.json` (paginated),
 * caches them in localStorage, and exposes a Set lookup for find-your-car-badge.
 *
 * Why not ship handles on every card? Product ↔ collection membership is
 * resolved server-side on fetch; the theme only needs numeric product ids for
 * the current selection.
 *
 * @fires voldt:car-match-products:ready - After cache is updated for the
 *   current selection (including empty fetch / cleared selection).
 */

(() => {
  'use strict';

  const STORAGE_KEY = 'voldt:car-match-product-ids';
  const CACHE_VERSION = 1;
  const MAX_PAGES = 50;

  /** @type {Promise<void>|null} */
  let inflight = null;

  function readCache() {
    try {
      const raw = localStorage.getItem(STORAGE_KEY);
      if (!raw) return null;
      const p = JSON.parse(raw);
      if (
        !p ||
        p.v !== CACHE_VERSION ||
        typeof p.modelHandle !== 'string' ||
        !Array.isArray(p.ids)
      ) {
        return null;
      }
      return p;
    } catch {
      return null;
    }
  }

  /**
   * @param {string} handle
   * @param {number[]} ids
   */
  function writeCache(handle, ids) {
    try {
      localStorage.setItem(
        STORAGE_KEY,
        JSON.stringify({
          v: CACHE_VERSION,
          modelHandle: handle,
          ids,
          fetchedAt: new Date().toISOString(),
        })
      );
    } catch (error) {
      console.warn('[CarMatchProductCache] Failed to persist cache', error);
    }
  }

  function clearCache() {
    try {
      localStorage.removeItem(STORAGE_KEY);
    } catch (_) {
      /* ignore */
    }
  }

  function dispatchReady() {
    document.dispatchEvent(
      new CustomEvent('voldt:car-match-products:ready', { bubbles: false })
    );
  }

  /**
   * @param {string} collectionUrl - Model collection URL (Liquid `collection.url`)
   * @returns {Promise<number[]>}
   */
  async function fetchAllProductIds(collectionUrl) {
    const base = new URL(collectionUrl, window.location.origin);
    const path = base.pathname.replace(/\/$/, '');
    const ids = [];
    let page = 1;

    for (;;) {
      const url = `${path}/products.json?limit=250&page=${page}`;
      const res = await fetch(url, {
        credentials: 'same-origin',
        headers: { Accept: 'application/json' },
      });
      if (!res.ok) {
        console.warn('[CarMatchProductCache] products.json failed', res.status, url);
        break;
      }
      const data = await res.json();
      const products = data.products || [];
      if (products.length === 0) break;
      for (const pr of products) {
        if (pr?.id != null) ids.push(Number(pr.id));
      }
      if (products.length < 250) break;
      page += 1;
      if (page > MAX_PAGES) break;
    }

    return ids;
  }

  /**
   * Ensures localStorage has product ids for the currently selected model handle.
   * @returns {Promise<void>}
   */
  async function ensureReady() {
    const sel = window.VoldtSelectedCar?.get?.() ?? null;
    if (!sel?.model?.handle || !sel.model.url) {
      clearCache();
      return;
    }

    const handle = sel.model.handle;
    const cache = readCache();
    if (cache && cache.modelHandle === handle) {
      return;
    }

    if (inflight) return inflight;

    inflight = (async () => {
      try {
        const ids = await fetchAllProductIds(sel.model.url);
        writeCache(handle, ids);
      } catch (error) {
        console.warn('[CarMatchProductCache] Fetch failed', error);
        writeCache(handle, []);
      } finally {
        inflight = null;
      }
    })();

    return inflight;
  }

  /**
   * @returns {Set<number>}
   */
  function getIdSet() {
    const sel = window.VoldtSelectedCar?.get?.() ?? null;
    if (!sel?.model?.handle) return new Set();
    const cache = readCache();
    if (!cache || cache.modelHandle !== sel.model.handle) return new Set();
    return new Set(cache.ids.map((n) => Number(n)));
  }

  function onSelectionChanged() {
    const sel = window.VoldtSelectedCar?.get?.() ?? null;
    if (!sel?.model?.handle) {
      clearCache();
      dispatchReady();
      return;
    }
    const cache = readCache();
    if (cache && cache.modelHandle !== sel.model.handle) {
      clearCache();
    }
    Promise.resolve(ensureReady()).then(() => dispatchReady());
  }

  document.addEventListener('voldt:car:changed', onSelectionChanged);

  if (document.readyState === 'loading') {
    document.addEventListener(
      'DOMContentLoaded',
      () => {
        Promise.resolve(ensureReady()).then(() => dispatchReady());
      },
      { once: true }
    );
  } else {
    Promise.resolve(ensureReady()).then(() => dispatchReady());
  }

  window.VoldtCarMatchProductCache = Object.freeze({
    ensureReady,
    getIdSet,
    clearCache,
  });
})();
})();
(function () {
/**
 * Trusted Shops widgets — deferred until idle to keep them off the LCP path.
 *
 * Mode comes from Theme settings → Trusted Shops:
 * - classic: eTrusted widget.js/v2 + classic Trustbadge (TSID from settings)
 * - app:     eTrusted widget.js/v2 only (floating badge from Shopify ScriptTag app)
 */


function appendScript(src, attrs) {
  if (document.querySelector('script[src="' + src + '"]')) return;
  const script = document.createElement('script');
  script.src = src;
  script.async = true;
  script.defer = true;
  if (attrs) {
    Object.keys(attrs).forEach((key) => {
      script.setAttribute(key, attrs[key]);
    });
  }
  document.head.appendChild(script);
}

scheduleIdle(() => {
  const config = window.__voldtTrustedShops || {};
  const mode = config.mode === 'app' ? 'app' : 'classic';
  const tsid = String(config.tsid || 'X3D7F5A6B1027296FB26A5F99D13329ED').trim();

  appendScript('https://integrations.etrusted.com/applications/widget.js/v2');

  if (mode !== 'classic' || !tsid) return;

  appendScript(`//widgets.trustedshops.com/js/${tsid}.js`, {
    'data-desktop-y-offset': '80',
    'data-mobile-y-offset': '80',
    'data-desktop-disable-reviews': 'false',
    'data-desktop-enable-custom': 'false',
    'data-desktop-position': 'right',
    'data-desktop-custom-width': '156',
    'data-desktop-enable-fadeout': 'false',
    'data-disable-mobile': 'false',
    'data-disable-trustbadge': 'false',
    'data-mobile-custom-width': '156',
    'data-mobile-disable-reviews': 'false',
    'data-mobile-enable-custom': 'false',
    'data-mobile-position': 'right',
    'data-color-scheme': 'os-default',
    charset: 'UTF-8',
  });
}, { timeout: 4000 });
})();
/**
 * Deferred bundle: non-critical scripts loaded at end of body.
 */












scheduleIdle(() => {
  import('./find-your-car-popup.js');
  import('./find-your-car-float.js');
  import('./find-your-car-badge.js');
}, { timeout: 3000 });

if (document.querySelector('related-products')) {
  const loadRelatedProducts = () => import('./related-products.js');
  if (document.querySelector('related-products[data-eager="true"]')) {
    loadRelatedProducts();
  } else {
    scheduleIdle(loadRelatedProducts, { timeout: 2000 });
  }
}
